import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Button, Input, Modal, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useApolloClient } from 'react-apollo';
import GET_NEXXEN_SEGMENT_SETS from '../../../core/GraphQl/Queries/GET_NEXXEN_SEGMENT_SETS';
import { SEGMENT_POOL } from '../../../core/strategyBlueprint/sharedConfig';

const { Text } = Typography;

// Normalize static strategy-pool segment sets into the same shape as live API
// results so they can be merged + deduped cleanly in the table. These are the
// universal "All Advertisers" sets the strategy builder pushes to the DSP.
const STATIC_SEGMENT_SETS = Object.values(SEGMENT_POOL).map((set) => ({
  id: set.segmentId,
  name: set.name,
  description: 'Universal strategy segment set',
  status: 'active',
  cpmCost: set.cost || 0,
  source: 'Universal',
}));

// Merge live advertiser-scoped sets with static universal sets, deduping by id.
// If the same set comes back from the live API, the live version wins (fresher
// cost/status) but keeps the Universal tag.
const mergeSegmentSets = (liveSets) => {
  const merged = new Map();
  STATIC_SEGMENT_SETS.forEach((set) => merged.set(set.id, { ...set }));
  (liveSets || []).forEach((set) => {
    const isUniversal = merged.has(set.id);
    merged.set(set.id, { ...set, source: isUniversal ? 'Universal' : 'Advertiser' });
  });
  return Array.from(merged.values());
};

const CampaignRequestorNexxenSegmentsDialog = ({
  visible,
  setVisible,
  onAddSelections,
  selectedSegments = [],
  advertiserDspId,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [segmentProgress, setSegmentProgress] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const client = useApolloClient();

  // Ref to track if component is mounted (prevent memory leaks)
  const isMountedRef = useRef(true);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleAddSelections = () => {
    const selectedItems = segments
      .filter((item) => selectedRowKeys.includes(item.id))
      .map((item) => ({
        segmentId: item.id,
        name: item.name,
        segmentType: 'segmentSet',
        cost: item.cpmCost || 0,
      }));

    onAddSelections(selectedItems);
    setVisible(false);
  };

  const rowSelection = {
    type: 'radio',
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // Fetch all segment sets using batched API calls (100 at a time).
  // Always merges in the static universal strategy-pool sets, so the dialog is
  // useful even when the selected advertiser has no Nexxen DSP advertiser
  // configured or the live query returns nothing.
  const fetchSegments = useCallback(async () => {
    if (!isMountedRef.current) return;

    setLoadingSegments(true);
    setSegmentProgress('Loading segment sets...');
    let allSegments = [];
    let offset = 0;
    const limit = 100;

    try {
      if (advertiserDspId) {
        while (isMountedRef.current) {
          const { data } = await client.query({
            query: GET_NEXXEN_SEGMENT_SETS,
            variables: {
              advertiserId: advertiserDspId,
              offset,
              limit,
            },
            fetchPolicy: 'network-only',
          });

          if (!isMountedRef.current) return;

          const pageData = data?.nexxenSegmentSets?.data || [];
          const total = data?.nexxenSegmentSets?.queryTotal || 0;

          allSegments = [...allSegments, ...pageData];
          setSegmentProgress(`Loaded ${allSegments.length} of ${total} segment sets...`);

          if (allSegments.length >= total || pageData.length === 0) break;
          offset += limit;
        }
      }

      if (isMountedRef.current) {
        setSegments(mergeSegmentSets(allSegments));
      }
    } catch (error) {
      console.error('Error fetching segment sets:', error);
      if (isMountedRef.current) {
        message.warning('Failed to load advertiser segment sets — showing universal sets only.');
        setSegments(mergeSegmentSets([]));
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingSegments(false);
        setSegmentProgress('');
      }
    }
  }, [client, advertiserDspId]);


  // Set mounted ref on mount, clear on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load data when modal opens (only if not already loaded). Runs even without
  // a configured DSP advertiser so the static universal sets still appear.
  useEffect(() => {
    if (visible && segments.length === 0) {
      fetchSegments();
    }
  }, [visible, segments.length, fetchSegments]);


  // Reset cached segment sets whenever the DSP advertiser changes. The initial
  // mount is skipped: this effect runs after the fetch effect on mount, and an
  // unconditional reset would wipe the universal sets that a synchronous
  // (no advertiserDspId) fetch just loaded, leaving an empty table.
  const prevAdvertiserDspIdRef = useRef(advertiserDspId);
  useEffect(() => {
    if (prevAdvertiserDspIdRef.current === advertiserDspId) return;
    prevAdvertiserDspIdRef.current = advertiserDspId;
    setSegments([]);
    setSelectedRowKeys([]);
  }, [advertiserDspId]);

  // Initialize selected row keys from props
  useEffect(() => {
    if (selectedSegments && selectedSegments.length > 0) {
      const keys = selectedSegments.map((s) => s.segmentId);
      setSelectedRowKeys(keys);
    } else {
      setSelectedRowKeys([]);
    }
  }, [selectedSegments, visible]);

  // Client-side filtering
  const filteredSegments = useMemo(() => {
    if (!searchTerm) return segments;
    const lowerSearch = searchTerm.toLowerCase();
    return segments.filter((item) =>
      item.id?.toString().includes(searchTerm) ||
      item.name?.toLowerCase().includes(lowerSearch) ||
      item.description?.toLowerCase().includes(lowerSearch)
    );
  }, [segments, searchTerm]);

  // Client-side pagination
  const paginatedSegments = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSegments.slice(start, start + pageSize);
  }, [filteredSegments, currentPage, pageSize]);

  const segmentColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 180,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 110,
      render: (source) =>
        source === 'Universal' ? (
          <Tag color="blue">Universal</Tag>
        ) : (
          <Tag>Advertiser</Tag>
        ),
    },
    // CPM Cost column removed — cpmCost is never populated (static pool
    // hardcodes 0, live Nexxen sets return 0). Re-add when real pricing exists.
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status || 'Unknown'}
        </Tag>
      ),
    },
  ];


  const handlePaginationChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  };

  // Reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 20,
          }}
        >
          <span>NEXXEN SEGMENT SETS</span>
          <div>
            <Button
              type="primary"
              disabled={selectedRowKeys.length === 0}
              onClick={() => handleAddSelections()}
            >
              Add Selection
            </Button>
          </div>
        </div>
      }
      visible={visible}
      onCancel={() => setVisible(false)}
      footer={null}
      width={900}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Text type="secondary">
          Select a segment set to add to this line item for audience targeting.
          Only one segment set can be attached per line item. Universal sets are
          available for all advertisers; Advertiser sets are scoped to the
          advertiser configured on this campaign.
        </Text>


        <Input
          placeholder="Search by segment set ID, name, or description..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />

        {loadingSegments ? (
          <Space direction="vertical" style={{ width: '100%', padding: 40 }} align="center">
            <Spin />
            <Text type="secondary">{segmentProgress || 'Loading segment sets...'}</Text>
          </Space>
        ) : (
          <Table
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
            columns={segmentColumns}
            dataSource={paginatedSegments}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredSegments.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: handlePaginationChange,
            }}
            scroll={{ y: 400 }}
            size="small"
          />
        )}
      </Space>
    </Modal>
  );
};

export default CampaignRequestorNexxenSegmentsDialog;
